import { createAction } from '@reduxjs/toolkit';

import type { AuthenticationStep, SSOMethod } from '@accounts/types/authentication';
import type {
    AuthenticationError,
    ResetSecondaryAuthenticationAction,
    SetAuthenticationStatusAction,
    SetErrorByPathAction,
    SetIsExpiredPasswordResetTransactionAction,
    SetIsValidPasswordResetTransactionAction,
    SetOnboardingInvitationAction,
    SetOnboardingTransactionAction,
    SetPasswordResetTransactionAction,
    SetPrimaryLoginErrorAction,
    SetQuickLoginAction,
    SetReturningSSOUserAction,
    SetSSOErrorAction,
    SetSSOMethodsAction,
    SignInAsDifferentUserAction,
    SubmitNewPasswordAction
} from '@accounts/types/store';

import { type Invitation } from '@type/invitations';
import { type Transaction } from '@type/transaction';

export const RESET_SECONDARY_AUTHENTICATION = 'RESET_SECONDARY_AUTHENTICATION';
export const SET_QUICK_LOGIN = 'SET_QUICK_LOGIN';

export const SET_SSO_METHODS = 'SET_SSO_METHODS';
export const SET_RETURNING_SSO_USER = 'SET_RETURNING_SSO_USER';
export const SET_SSO_ERROR = 'SET_SSO_ERROR';

export const SET_AUTHENTICATION_STATUS = 'SET_AUTHENTICATION_STATUS';
export const SET_PRIMARY_LOGIN_ERROR = 'SET_PRIMARY_LOGIN_ERROR';

export const SIGN_IN_AS_DIFFERENT_USER = 'SIGN_IN_AS_DIFFERENT_USER';

export const SET_IS_EXPIRED_PASSWORD_RESET_TRANSACTION =
    'SET_IS_EXPIRED_PASSWORD_RESET_TRANSACTION';
export const SET_IS_VALID_PASSWORD_RESET_TRANSACTION = 'SET_IS_VALID_PASSWORD_RESET_TRANSACTION';
export const SET_PASSWORD_RESET_TRANSACTION = 'SET_PASSWORD_RESET_TRANSACTION';
export const SUBMIT_NEW_PASSWORD = 'SUBMIT_NEW_PASSWORD';

export const SET_ONBOARDING_TRANSACTION = 'SET_ONBOARDING_TRANSACTION';
export const SET_ONBOARDING_INVITATION = 'SET_ONBOARDING_INVITATION';

export const SET_ERROR_BY_PATH = 'SET_ERROR_BY_PATH';

export const resetSecondaryAuthentication = (): ResetSecondaryAuthenticationAction => ({
    type: RESET_SECONDARY_AUTHENTICATION
});

export const setQuickLogin = (payload: boolean): SetQuickLoginAction => ({
    type: SET_QUICK_LOGIN,
    payload
});

export const setPrimaryLoginError = (payload: AuthenticationError): SetPrimaryLoginErrorAction => ({
    type: SET_PRIMARY_LOGIN_ERROR,
    payload
});

export const setStep = createAction<AuthenticationStep | undefined, 'setStep'>('setStep');

export const setSSOMethods = (payload: SSOMethod[]): SetSSOMethodsAction => ({
    type: SET_SSO_METHODS,
    payload
});

export const setSSOError = (payload: AuthenticationError): SetSSOErrorAction => ({
    type: SET_SSO_ERROR,
    payload
});

export const setIsValidPasswordResetTransaction = (
    payload: boolean
): SetIsValidPasswordResetTransactionAction => ({
    type: SET_IS_VALID_PASSWORD_RESET_TRANSACTION,
    payload
});

export const setIsExpiredPasswordResetTransaction = (
    payload: boolean
): SetIsExpiredPasswordResetTransactionAction => ({
    type: SET_IS_EXPIRED_PASSWORD_RESET_TRANSACTION,
    payload
});

export const setPasswordResetTransaction = (
    payload: Transaction
): SetPasswordResetTransactionAction => ({
    type: SET_PASSWORD_RESET_TRANSACTION,
    payload
});

export const initializeOnboarding = createAction<string | undefined, 'initializeOnboarding'>(
    'initializeOnboarding'
);

export const setOnboardingTransaction = (payload: Transaction): SetOnboardingTransactionAction => ({
    type: SET_ONBOARDING_TRANSACTION,
    payload
});

export const setIsValidOnboardingInvitation = createAction<
    boolean,
    'setIsValidOnboardingInvitation'
>('setIsValidOnboardingInvitation');

export const setIsExpiredOnboardingTransaction = createAction<
    boolean,
    'setIsExpiredOnboardingTransaction'
>('setIsExpiredOnboardingTransaction');

export const setOnboardingInvitation = (payload: Invitation): SetOnboardingInvitationAction => ({
    type: SET_ONBOARDING_INVITATION,
    payload
});

export const setPreviousSecondaryProofId = createAction<
    string | undefined,
    'setPreviousSecondaryProofId'
>('setPreviousSecondaryProofId');

export const resetAuthetication = createAction('resetAuthetication');

export type AuthenticationActions =
    | ResetSecondaryAuthenticationAction
    | SetAuthenticationStatusAction
    | SetQuickLoginAction
    | SetPrimaryLoginErrorAction
    | SetSSOMethodsAction
    | SetReturningSSOUserAction
    | SetSSOErrorAction
    | SignInAsDifferentUserAction
    | SetIsExpiredPasswordResetTransactionAction
    | SetIsValidPasswordResetTransactionAction
    | SetPasswordResetTransactionAction
    | SubmitNewPasswordAction
    | SetOnboardingTransactionAction
    | SetOnboardingInvitationAction
    | SetErrorByPathAction
    | ReturnType<typeof setIsValidOnboardingInvitation>
    | ReturnType<typeof initializeOnboarding>
    | ReturnType<typeof setIsExpiredOnboardingTransaction>
    | ReturnType<typeof setStep>
    | ReturnType<typeof setPreviousSecondaryProofId>
    | ReturnType<typeof resetAuthetication>;
